const week5 = {
  number: 5,
  title: "Direction",
  theme: "The organizing principle that precedes all choice",
  modules: [
    {
      id: "5.1",
      title: "The Primacy of Direction",
      content: `Before there are goals, before there are plans, before there are decisions—there is direction.

**Direction is the mind's first organizing principle.** It is the internal lean that says "there" rather than "here."

Direction is not a goal. Goals are specific, measurable, and achievable or not. Direction is none of these things. Direction is orientation—a sense of which way is forward.

Consider: You can have a goal without a direction. You can decide to run a marathon because you think you should, because someone else wants you to, because you read that it's good for you. There is a goal, but no internal pull toward it. This goal will be difficult to sustain because it is disconnected from direction.

And you can have a direction without a goal. You can sense that you are moving toward something—creativity, connection, growth, service—without knowing exactly what that will look like. There is no specific destination, but there is a pull.

**Direction is the precondition that makes goals possible.** When direction is present, goals become expressions of it—ways of moving in the direction you are already pulled. When direction is absent, goals become substitutes for it—attempts to manufacture meaning through achievement.

This is why some people achieve everything and feel empty. They pursued goals without direction. They arrived at destinations that did not actually represent somewhere they wanted to be.

Direction is sensed, not chosen.

You do not sit down and decide your direction. You notice it. It emerges from the combination of your residents, your experiences, your values, and something harder to name—a pull that is unique to you.

The work is not to create direction but to recognize it—to clear away the noise that obscures it, to hear it underneath the competing voices of should and must and ought.`
    },
    {
      id: "5.2",
      title: "Orientation, Vector, Slope",
      content: `Direction has three components:

**Orientation** — the sense of which way is forward

Orientation is the compass heading. It tells you which direction represents movement and which represents stagnation or retreat. Without orientation, you can move but you cannot navigate—every direction feels the same.

Orientation answers: Where is "there" relative to "here"?

**Vector** — the pull toward action

Vector is the strength of the pull. Some directions call strongly; others are merely present. Some people feel their direction as an urgent pull; others as a quiet inclination.

Vector answers: How strongly am I drawn in this direction?

**Slope** — the effort required to move

Slope is the difficulty of the terrain. Even when orientation is clear and vector is strong, the slope may be steep. Movement in the right direction may require great effort—or it may feel like flowing downhill.

Slope answers: What will this movement cost me?

**These three components together determine whether a person feels:**

- **Stagnant** — Orientation unclear, vector weak, slope irrelevant because there is no movement. "I don't know which way to go."

- **Pulled** — Orientation clear, vector strong, slope manageable. "I know where I'm going and I want to get there."

- **Blocked** — Orientation clear, vector strong, slope too steep. "I know where I want to go but I can't seem to move."

- **Drifting** — Orientation unclear, vector weak, slope low. "Things are easy but I'm not going anywhere meaningful."

- **Overwhelmed** — Orientation unclear, vector diffuse (pulled in multiple directions), slope steep. "Everything is hard and I don't know which way to go."

Understanding these components helps diagnose navigation problems:

- If orientation is the problem, the work is clarification—listening for direction underneath the noise.
- If vector is the problem, the work is connection—reconnecting with why this direction matters.
- If slope is the problem, the work is pacing—accepting that movement is possible but requires sustained effort.

Many people treat navigation problems as if they are all slope problems—they try harder. But trying harder does not help when orientation is unclear or vector is weak. The work must match the problem.`
    },
    {
      id: "5.3",
      title: "Direction vs. Goals",
      content: `The culture is obsessed with goals. Set goals. Achieve goals. Measure progress toward goals. Success is hitting targets.

But there is something the goal-obsessed culture misses:

**A direction can exist without a goal. A goal cannot exist without a direction.**

Direction is primary; goals are secondary. Goals are expressions of direction—ways of concretizing it, making it actionable, translating it into steps. But they are not the thing itself.

Here is what happens when goals substitute for direction:

- You achieve the goal and feel empty—because the goal was never connected to where you actually wanted to go.
- You fail to achieve the goal and feel destroyed—because you had no direction to orient you beyond the specific target.
- You change goals constantly—because without direction, one goal is as good as another.
- You achieve goals and immediately set new ones—because achievement does not satisfy; only movement in a meaningful direction satisfies.

**Goals fight; direction guides.**

Multiple goals compete for time and energy. You can only work on one goal at a time, so goals create conflict: this or that, now or later, here or there.

Direction does not compete. Direction is the organizing principle that tells you which goals to pursue, in what order, and when to abandon goals that no longer serve. Direction guides the selection and prioritization of goals rather than being one more item competing for attention.

**Process respects uncertainty; goals demand certainty.**

Goals require specificity: what exactly, by when exactly, measured how exactly. This specificity works well for things that can be known in advance.

But the most important aspects of human life resist this specificity. "Become a loving person" is not a goal—it is a direction. "Build something meaningful" is not a goal—it is a direction. "Live authentically" is not a goal—it is a direction.

Direction operates through process: move in the direction, adjust based on what you learn, move again. Goals try to define the endpoint in advance. For navigational questions, direction is the appropriate frame.`
    },
    {
      id: "5.4",
      title: "Direction vs. Desire",
      content: `Desire is not the same as direction.

This distinction is crucial because they are easily confused. Both pull you toward something. Both create movement. Both feel meaningful in the moment.

But they are different in kind.

**Desire is emotional intensity.** It is the feeling of wanting something—sometimes overwhelming, sometimes subtle, but always a feeling. Desire can be triggered, created, manipulated. Advertising exists because desire can be manufactured.

**Direction is structural orientation.** It is not a feeling but a position—a vector in the space of possible movements. Direction remains even when the feeling of desire fades.

Here is how to tell the difference:

- **Desire fluctuates; direction persists.** You may desire something intensely today and forget about it tomorrow. Direction does not fluctuate this way. It remains as the underlying pull even when intensity varies.

- **Desire is about the object; direction is about the movement.** Desire fixates on what you want—the specific thing, the specific outcome. Direction is about the trajectory—where you are going, not where you will arrive.

- **Satisfying desire ends the pull; following direction deepens it.** When you get what you desire, the desire goes away (at least temporarily). When you move in your direction, the direction often becomes clearer and the pull becomes stronger.

- **Desire can be intense but provide no orientation. Direction can be quiet but shape an entire life.**

Many people confuse desire for direction and build their lives around satisfying desires. They achieve, acquire, consume—and find that satisfaction is always temporary. The desire was real, but it was not direction.

The question is not "What do I want?" but "Where am I going?"

Wants change. Direction—true direction—emerges from something deeper than temporary wanting. It is the pull that remains when you strip away what you think you should want, what others want for you, what would be impressive or successful or acceptable.

Direction is what you find when you ask not "What do I desire?" but "What pulls me even when I don't feel desire?"`
    },
    {
      id: "5.5",
      title: "Finding Your Direction",
      content: `You do not set a direction. You recognize one.

This is counterintuitive. The culture tells you to choose your direction, decide your path, determine your goals. But direction is not a choice in that sense. Direction is a pull that already exists—your work is to notice it.

**Why is direction obscured?**

- **Noise** — The constant input of what you should want, what others want for you, what is practical, what is acceptable. This noise drowns out the signal of direction.

- **Fear** — The Trust Resident may have learned that moving in certain directions is dangerous. The fear does not eliminate the direction; it obscures it.

- **Disconnection** — When the Ground Floor runs everything, the Upper Floor's sense of direction becomes inaccessible. You function but do not navigate.

- **Old commitments** — Directions set in the past may no longer apply, but you continue following them because they are familiar. The old direction blocks the current one.

**How to recognize direction:**

**Listen for the pull that persists.** What draws your attention even when you try to ignore it? What do you find yourself moving toward even without deciding to? What keeps coming back?

**Notice what energizes vs. what depletes.** Direction feels like energy, even when the slope is steep. Movement in the wrong direction depletes even when the terrain is easy.

**Attend to friction.** When your stated goals conflict with something inside you, the friction may be a signal that the goal does not align with your actual direction.

**Consider: What would you move toward if no one were watching?** Strip away the performance, the should, the need to impress or satisfy. What remains?

**Ask the residents.** The Trust Resident knows what direction feels safe. The Initiative Resident knows what direction feels exciting. The Identity Resident knows what direction feels authentic. The Generativity Resident knows what direction feels meaningful. Their answers may differ—and the friction between them may point to something true.

Direction is not found through thinking alone. It is sensed—through attention, through movement, through the gradual clearing of noise until the signal becomes audible.

**You do not need to know your direction completely to begin navigating.** You need only a sense—a pull that suggests which way is forward from here. The rest becomes clear through movement.`
    }
  ],
  reflection: {
    prompt: "Forget your goals for a moment. What direction are you already leaning? What does your system already want to move toward, independent of what you've told yourself you should want?",
    placeholder: "Try to sense underneath the goals and desires. What pull persists? What draws you even when you try to ignore it?"
  }
};

export default week5;
